import React, { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useHistory } from 'react-router-dom';
import DynamicFormFromJSON from 'components/common/form/DynamicFormFromJSON';
import RFQChecklistOptionHeader from 'components/common/headers/RFQChecklistOptionHeader';
import { useQueryParams } from '@insurancemenu/imenu-components-lib';
import { useRFQConfigurePlanAttributesStore } from 'store/pages/rfq/rfq-template/configure-plan-attribute/pageStore';
import { CHECKLIST_HEADER_TEST_ID, ATTRIBUTES_PROGRESS_STATUS_TEST_ID } from 'components/common/rfq/constants';
import useRfqTemplate from 'global/hooks/useRfqTemplate';
import { PlanAttributeCategory } from 'types/productServiceV2';
import { createStyles } from './styles';
import { CONFIGURE_ATTRIBUTE_FORM_TEST_ID } from '../constants';
import EditAttributeFormFooter from './EditAttributeFormFooter/index';
import CreateAttributeFormFooter from './CreateAttributeFormFooter';
import { convertTag } from '../../../../global/utils/convertTag';

const RFQConfigureAttributeForm = observer((): JSX.Element => {
  // Common data
  const classes = createStyles();
  const { templateId, pageType } = useRfqTemplate();

  const history = useHistory();
  const query = useQueryParams();

  const attributeId = query.get('attributeId') || '';
  const attributeCategory: PlanAttributeCategory | null = query.get(
    'attributeCategory',
  ) as PlanAttributeCategory | null;

  // Mobx store
  const {
    isLoading,
    form,
    dynamicForm,
    currentAttributeName,
    attributesProgressStatus,
    canReturnToPreviousForm,
    canProceedWithNextForm,
    getInitialData,
    resetInitialData,
  } = useRFQConfigurePlanAttributesStore();

  useEffect(() => {
    void getInitialData(templateId, attributeId, attributeCategory);
    return resetInitialData;
  }, []);

  const returnBack = (): void => {
    history.push(`/plan-attributes/${templateId}?type=${pageType}`);
  };

  return (
    <>
      <RFQChecklistOptionHeader
        title="Configure Attribute"
        subtitle={`${currentAttributeName} (${convertTag(attributeCategory || '')})`}
        onRedirectBack={returnBack}
        className={classes.header}
        data-test-id={CHECKLIST_HEADER_TEST_ID}
      />
      <h3 className={classes.attributesProgressStatus} data-test-id={ATTRIBUTES_PROGRESS_STATUS_TEST_ID}>
        {attributesProgressStatus}
      </h3>
      <DynamicFormFromJSON
        data-test-id={CONFIGURE_ATTRIBUTE_FORM_TEST_ID}
        dynamicForm={dynamicForm}
        form={form}
        loading={isLoading}
        hideCommonFormFooter
        customFormFooter={(isFormValid: boolean) => {
          return attributeId ? (
            <EditAttributeFormFooter
              isFormValid={isFormValid}
              canProceedWithNextForm={canProceedWithNextForm}
              canReturnToPreviousForm={canReturnToPreviousForm}
              returnBack={returnBack}
            />
          ) : (
            <CreateAttributeFormFooter
              isFormValid={isFormValid}
              returnBack={returnBack}
              updateData={() => {
                void getInitialData(templateId, attributeId, attributeCategory);
              }}
            />
          );
        }}
      />
    </>
  );
});

export default RFQConfigureAttributeForm;
